
export const MARKER_BOX_WIDTH = 30

export const MARKER_LANE_STEP = 26


export const MAX_MARKER_LANES = 2

export type MarkerSpan = { start: number; end: number }

export type HorizontalOffsetOptions = {
  gap?: number
  maxX?: number
}

const DEFAULT_GAP = 2


export function resolveHorizontalOffsets(
  boxes: ReadonlyArray<MarkerSpan | null>,
  blocked: ReadonlyArray<MarkerSpan>,
  options: HorizontalOffsetOptions = {},
): number[] {
  const gap = options.gap ?? DEFAULT_GAP
  const maxX = options.maxX ?? Infinity

  const offsets = new Array<number>(boxes.length).fill(0)

  const occupied: MarkerSpan[] = blocked
    .filter((s) => Number.isFinite(s.start) && Number.isFinite(s.end))
    .map((s) => ({ start: s.start, end: s.end }))

  const findConflict = (start: number, end: number): MarkerSpan | undefined =>
    occupied.find((s) => start < s.end + gap && end > s.start - gap)

  const order = boxes
    .map((box, index) => ({ box, index }))
    .filter((e): e is { box: MarkerSpan; index: number } =>
      !!e.box && Number.isFinite(e.box.start) && Number.isFinite(e.box.end),
    )
    .sort((a, b) => a.box.start - b.box.start || a.index - b.index)

  for (const entry of order) {
    const width = entry.box.end - entry.box.start

    let start = entry.box.start
    for (let guard = 0; guard <= occupied.length; guard++) {
      const conflict = findConflict(start, start + width)
      if (!conflict) break
      start = conflict.end + gap
    }

    if (start + width > maxX) {
      start = entry.box.start
      for (let guard = 0; guard <= occupied.length; guard++) {
        const conflict = findConflict(start, start + width)
        if (!conflict) break
        start = conflict.start - gap - width
      }
      start = Math.max(0, start)
    }

    occupied.push({ start, end: start + width })
    offsets[entry.index] = start - entry.box.start
  }

  return offsets
}

export type PointLaneOptions = {
  boxWidth?: number
  maxLanes?: number
}

export function assignPointLanes(
  xs: ReadonlyArray<number>,
  options: PointLaneOptions = {},
): number[] {
  const width = options.boxWidth ?? MARKER_BOX_WIDTH
  const half = width / 2
  const maxLanes = Math.max(1, options.maxLanes ?? MAX_MARKER_LANES)

  const lanes = new Array<number>(xs.length).fill(0)

  const order = xs
    .map((x, i) => ({ x, i }))
    .filter((p) => Number.isFinite(p.x))
    .sort((a, b) => a.x - b.x || a.i - b.i)

  const lastRight: number[] = []

  for (const point of order) {
    const left = point.x - half

    let lane = -1
    for (let l = 0; l < lastRight.length; l++) {
      if (lastRight[l] <= left) {
        lane = l
        break
      }
    }

    if (lane === -1) {
      if (lastRight.length < maxLanes) {
        lane = lastRight.length
        lastRight.push(-Infinity)
      } else {
        lane = 0
        for (let l = 1; l < lastRight.length; l++) {
          if (lastRight[l] < lastRight[lane]) lane = l
        }
      }
    }

    lastRight[lane] = point.x + half
    lanes[point.i] = lane
  }

  return lanes
}
